import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useOffer } from "./offers/useOffer";
import { dismissOffer, setMuted } from "./offers/offerPrefs";
import { useAssistantConversation } from "../../modules/assistant/state/AssistantConversationProvider";

/**
 * Dismissible, rate-limited offer bubble shown beside the docked Mochi.
 * `useOffer()` reads session/local storage (dismissed set + mute flag) that
 * this component itself mutates on dismiss/mute — those writes don't trigger
 * a React re-render on their own, so a purely-local "tick" state is bumped
 * after each write to force `useOffer()` to re-evaluate and (when it now
 * returns null) unmount the bubble.
 */
export function MochiOfferBubble() {
  const { t } = useTranslation();
  const chat = useAssistantConversation();
  const [, setTick] = useState(0);
  const offer = useOffer();

  if (!offer) return null;

  const engage = () => {
    if (offer.sendsPrompt !== false && chat.status !== "streaming") {
      const prompt = t(offer.promptKey, offer.promptVars ?? {});
      void chat.sendMessage(prompt);
    }
    window.dispatchEvent(new CustomEvent("weco:mochi:open"));
  };

  const handleDismiss = () => {
    dismissOffer(offer.key);
    setTick((n) => n + 1);
  };

  const handleMute = () => {
    setMuted(true);
    setTick((n) => n + 1);
  };

  return (
    <div
      role="status"
      aria-label={t("assistant.mochi.offers.bubbleAria")}
      className="anim-fade-up mb-2 max-w-[18rem] rounded-3xl rounded-br-md border border-border
                 bg-background px-3 py-2 text-sm text-foreground shadow-xl"
      style={{ pointerEvents: "auto" }}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{t(offer.titleKey)}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t(offer.bodyKey)}</p>
        </div>
        <button
          type="button"
          aria-label={t("assistant.mochi.offers.dismiss")}
          className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={engage}
          className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:opacity-90"
        >
          {t(offer.ctaKey)}
        </button>
        <button
          type="button"
          onClick={handleMute}
          className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
        >
          {t("assistant.mochi.offers.dontShow")}
        </button>
      </div>
    </div>
  );
}
